import type {
  FlashlightAssignmentRow,
  FlashlightAssignmentYear,
  FlashlightAssignmentsResponse,
  FlashlightBenchmarkYear,
  FlashlightBenchmarksResponse,
} from './flashlightBenchmarks';

export type FlashlightDomainKey = 'speaking' | 'writing';
export const FLASHLIGHT_DOMAINS: FlashlightDomainKey[] = ['speaking', 'writing'];

export type FlashlightWindow = 'boy' | 'moy' | 'eoy';
export const FLASHLIGHT_WINDOWS: FlashlightWindow[] = ['boy', 'moy', 'eoy'];

export type WindowScores = { boy: number | null; moy: number | null; eoy: number | null };
export type Indicator = { name: string; scores: WindowScores };
export type BenchmarkOnTrack = { later: 'moy' | 'eoy'; onTrack: boolean } | null;
export type DomainBenchmark = { domain: FlashlightDomainKey; indicators: Indicator[]; average: WindowScores; onTrack?: BenchmarkOnTrack; windows?: { boy?: string; moy?: string; eoy?: string } };
export type Assignment = { assignmentId: string; name: string; date: string; subDomain: string; score: number };

export const RECENT_ASSIGNMENTS_PER_DOMAIN = 8;
export type DomainAssignments = { domain: FlashlightDomainKey; assignments: Assignment[] };

export type FlashlightData = {
  hasData: boolean;
  hasBenchmarks: boolean;
  hasAssignments: boolean;
  benchmarks: DomainBenchmark[];
  assignments: DomainAssignments[];
  progression: Record<FlashlightDomainKey, WindowScores>;
};

const EMPTY_WINDOW: WindowScores = { boy: null, moy: null, eoy: null };

function emptyFlashlightData(): FlashlightData {
  return {
    hasData: false,
    hasBenchmarks: false,
    hasAssignments: false,
    benchmarks: [],
    assignments: [],
    progression: { speaking: EMPTY_WINDOW, writing: EMPTY_WINDOW },
  };
}

function scoredYears(payload: FlashlightBenchmarksResponse | null | undefined): FlashlightBenchmarkYear[] {
  if (!payload || !payload.hasData) return [];
  return payload.years
    .filter((year) => year.benchmarks.length > 0)
    .sort((a, b) => b.schoolYear - a.schoolYear);
}

function assignedYears(payload: FlashlightAssignmentsResponse | null | undefined): FlashlightAssignmentYear[] {
  if (!payload || !payload.hasData) return [];
  return payload.years
    .filter((year) => year.assignments.some((domain) => domain.assignments.length > 0))
    .sort((a, b) => b.schoolYear - a.schoolYear);
}

function pickYear(
  benchmarks: FlashlightBenchmarksResponse | null | undefined,
  latestYear: number | null | undefined,
  assignments?: FlashlightAssignmentsResponse | null,
): number | null {
  const years = [...scoredYears(benchmarks).map((y) => y.schoolYear), ...assignedYears(assignments).map((y) => y.schoolYear)];
  if (years.length === 0) return null;
  if (latestYear != null && years.includes(latestYear)) return latestYear;
  return Math.max(...years);
}

export function flashlightYear(
  benchmarks: FlashlightBenchmarksResponse | null | undefined,
  latestYear: number | null | undefined,
  assignments?: FlashlightAssignmentsResponse | null,
): number | null {
  return pickYear(benchmarks, latestYear, assignments);
}

export function formatAssignmentDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${String(d.getUTCFullYear()).slice(2)}`;
}

function recentAssignments(rows: FlashlightAssignmentRow[]): Assignment[] {
  const kept = new Set<string>();
  const out: Assignment[] = [];
  for (const row of rows) {
    if (!kept.has(row.assignmentId)) {
      if (kept.size >= RECENT_ASSIGNMENTS_PER_DOMAIN) continue;
      kept.add(row.assignmentId);
    }
    out.push({
      assignmentId: row.assignmentId,
      name: row.name,
      date: formatAssignmentDate(row.date),
      subDomain: row.subDomain,
      score: row.score,
    });
  }
  return out;
}

export function getFlashlightData(
  benchmarks?: FlashlightBenchmarksResponse | null,
  latestYear?: number | null,
  assignments?: FlashlightAssignmentsResponse | null,
): FlashlightData {
  const year = pickYear(benchmarks, latestYear, assignments);
  if (year == null) return emptyFlashlightData();
  const benchmarkYear = scoredYears(benchmarks).find((y) => y.schoolYear === year);
  const assignmentYear = assignedYears(assignments).find((y) => y.schoolYear === year);
  const progression: Record<FlashlightDomainKey, WindowScores> = { speaking: EMPTY_WINDOW, writing: EMPTY_WINDOW };
  for (const benchmark of benchmarkYear?.benchmarks ?? []) {
    progression[benchmark.domain] = benchmark.average;
  }
  const domainAssignments: DomainAssignments[] = (assignmentYear?.assignments ?? [])
    .filter((domain) => domain.assignments.length > 0)
    .map((domain) => ({ domain: domain.domain, assignments: recentAssignments(domain.assignments) }));
  const yearBenchmarks = benchmarkYear?.benchmarks ?? [];
  return {
    hasData: true,
    hasBenchmarks: yearBenchmarks.length > 0,
    hasAssignments: domainAssignments.length > 0,
    benchmarks: yearBenchmarks,
    assignments: domainAssignments,
    progression,
  };
}

export function flashlightBenchmark(
  data: FlashlightData,
  domain: FlashlightDomainKey,
): DomainBenchmark | undefined {
  return data.benchmarks.find((b) => b.domain === domain);
}

export function isFlashlightDomain(domain: string): domain is FlashlightDomainKey {
  return domain === 'speaking' || domain === 'writing';
}

export const FLASHLIGHT_VALUE_MESSAGE =
  'Speaking and Writing here reflect annual WIDA ACCESS results. Flashlight Benchmarks would add a within-year view of how this student is progressing between state tests.';
